import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Settings, User, Shield, Bell, Palette, Mail, Server, Lock, Send, CheckCircle, XCircle, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function SettingsPage() {
  const { user } = useAuth();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // SMTP Settings
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'CARFEIS',
    smtp_use_tls: true,
    is_configured: false,
  });
  const [loadingSMTP, setLoadingSMTP] = useState(true);
  const [savingSMTP, setSavingSMTP] = useState(false);
  const [testingSMTP, setTestingSMTP] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchSMTPSettings();
    }
  }, [user]);

  const fetchSMTPSettings = async () => {
    setLoadingSMTP(true);
    try {
      const response = await api.get('/settings/smtp');
      setSmtpSettings(response.data);
    } catch (error) {
      console.error('Erreur chargement SMTP');
    } finally {
      setLoadingSMTP(false);
    }
  };

  const handleSaveSMTP = async () => {
    setSavingSMTP(true);
    try {
      const response = await api.post('/settings/smtp', smtpSettings);
      toast.success(response.data.message);
      setSmtpSettings(prev => ({ ...prev, is_configured: response.data.is_configured }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingSMTP(false);
    }
  };

  const handleTestSMTP = async () => {
    setTestingSMTP(true);
    try {
      const response = await api.post('/settings/smtp/test');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de test SMTP');
    } finally {
      setTestingSMTP(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordDialog(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrateur',
      direction: 'Direction',
      teacher: 'Enseignant',
      accounting: 'Comptabilité',
      parent: 'Parent',
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground">Paramètres</h1>
        <p className="text-muted-foreground text-sm">Gérez vos préférences et votre compte</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profile" className="gap-2">
            <User className="w-4 h-4" />
            Profil
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="smtp" className="gap-2">
              <Mail className="w-4 h-4" />
              Email (SMTP)
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Profil utilisateur
                </CardTitle>
                <CardDescription>Vos informations personnelles</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">
                      {user?.first_name} {user?.last_name}
                    </h3>
                    <p className="text-muted-foreground">{user?.email}</p>
                    <Badge className="mt-1">{getRoleLabel(user?.role)}</Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Téléphone</p>
                    <p className="font-medium">{user?.phone || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Rôle</p>
                    <p className="font-medium">{getRoleLabel(user?.role)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Statut</p>
                    <Badge variant="outline" className="bg-green-100 text-green-700">
                      {user?.is_active !== false ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Sécurité
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="outline" 
                    className="w-full rounded-full" 
                    onClick={() => setShowPasswordDialog(true)}
                  >
                    Changer le mot de passe
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Notifications activées par défaut
                  </p>
                  <Button variant="outline" className="w-full rounded-full" disabled>
                    Gérer les notifications
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* SMTP Tab (Admin only) */}
        {user?.role === 'admin' && (
          <TabsContent value="smtp">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Configuration SMTP
                    </CardTitle>
                    <CardDescription>
                      Paramètres pour l'envoi d'emails (notifications, réinitialisations de mot de passe)
                    </CardDescription>
                  </div>
                  <Badge variant={smtpSettings.is_configured ? "default" : "secondary"} className="gap-1">
                    {smtpSettings.is_configured ? (
                      <><CheckCircle className="w-3 h-3" /> Configuré</>
                    ) : (
                      <><XCircle className="w-3 h-3" /> Non configuré</>
                    )}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Server className="w-4 h-4" />
                      Serveur SMTP
                    </Label>
                    <Input
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_host: e.target.value})}
                      placeholder="smtp.gmail.com"
                    />
                    <p className="text-xs text-muted-foreground">
                      Exemples: smtp.gmail.com, smtp.office365.com
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Port SMTP</Label>
                    <Input
                      type="number"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_port: parseInt(e.target.value) || 587})}
                      placeholder="587"
                    />
                    <p className="text-xs text-muted-foreground">587 (TLS) ou 465 (SSL)</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Utilisateur SMTP
                    </Label>
                    <Input
                      value={smtpSettings.smtp_user}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_user: e.target.value})}
                      placeholder="votre-email@gmail.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Mot de passe SMTP
                    </Label>
                    <Input
                      type="password"
                      value={smtpSettings.smtp_password}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email expéditeur</Label>
                    <Input
                      type="email"
                      value={smtpSettings.smtp_from_email}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_from_email: e.target.value})}
                      placeholder="noreply@carfeis.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom expéditeur</Label>
                    <Input
                      value={smtpSettings.smtp_from_name}
                      onChange={(e) => setSmtpSettings({...smtpSettings, smtp_from_name: e.target.value})}
                      placeholder="CARFEIS"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between border rounded-lg p-4">
                  <div>
                    <Label>Utiliser TLS</Label>
                    <p className="text-sm text-muted-foreground">Connexion sécurisée (recommandé)</p>
                  </div>
                  <Switch
                    checked={smtpSettings.smtp_use_tls}
                    onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, smtp_use_tls: checked})}
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                  <Button onClick={handleSaveSMTP} disabled={savingSMTP} className="gap-2">
                    <Save className="w-4 h-4" />
                    {savingSMTP ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleTestSMTP} 
                    disabled={testingSMTP || !smtpSettings.is_configured}
                    className="gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {testingSMTP ? 'Envoi en cours...' : 'Tester la configuration'}
                  </Button>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 mt-4">
                  <h4 className="font-medium mb-2">Configuration Gmail</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Activer l'authentification à 2 facteurs sur votre compte Google</li>
                    <li>Aller dans Compte Google → Sécurité → Mots de passe des applications</li>
                    <li>Créer un nouveau mot de passe pour "Courrier"</li>
                    <li>Utiliser ce mot de passe dans le champ "Mot de passe SMTP"</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Dialog: Changer mot de passe */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le mot de passe</DialogTitle>
            <DialogDescription>Entrez votre mot de passe actuel et le nouveau</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Annuler</Button>
            <Button onClick={handleChangePassword} disabled={changingPassword}>
              {changingPassword ? 'Modification...' : 'Modifier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
